import React, { FC, useEffect, useMemo, useState } from 'react';

import {
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Form as FormAntd,
  message,
  Table,
  Typography,
  UploadProps,
} from 'antd';
import { read, utils } from 'xlsx';
import dayjs from 'dayjs';

import { ActionButton } from '@hdi-seguros/web-components.action-button';
import {
  ColSize,
  ContentSection,
  DatePickerForm,
  DraggerUploadFile,
  Form,
  InputForm,
  RowSize,
  SelectForm,
} from '../../../../../../../../../../../shared/components';
import { Modal } from '@hdi-seguros/web-components.modal';

import {
  validateCuil,
  ValidateForm,
} from '../../../../../../../../../../../shared/utils';

import { SelectOption } from '../../../../../../../../../../../shared/interfaces';

import { COLORS } from '../../../../../../../../../../../config/themes/colors';

const S3_URL = process.env.REACT_APP_S3_URL;

interface InsuredProps {
  genderList: SelectOption[];
  isLoadingGenderList: boolean;
  onInsuredChange?: (insured: any[]) => void;
  onUploadFileChange?: (uploadFile: Blob | undefined) => void;
}

interface ErrorUpload {
  row: number;
  errorsMessage: string[];
}

const documentTypeList = [{ label: 'CUIL', value: 'cuil' }];

interface InsuredTableItem {
  birthdayDate: string;
  documentNumber: string;
  documentType: string;
  fullName: string;
  gender: string;
  key: string;
}

export const Insured: FC<InsuredProps> = ({
  genderList,
  isLoadingGenderList,
  onInsuredChange,
  onUploadFileChange,
}) => {
  const validateForm = new ValidateForm();

  const [canManuallyAdd, setCanManuallyAdd] = useState<boolean>(true);
  const [canShowClearTableModal, setCanShowClearTableModal] =
    useState<boolean>(false);
  const [canShowInsuredModal, setCanShowInsuredModal] =
    useState<boolean>(false);
  const [canShowUploadingFile, setCanShowUploadingFile] =
    useState<boolean>(false);
  const [canShowUploadModal, setCanShowUploadModal] = useState<boolean>(false);
  const [canUploadFiles, setCanUploadFiles] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [errorUploadFile, setErrorUploadFile] = useState<ErrorUpload[]>([]);
  const [insured, setInsured] = useState<Array<any>>([]);
  const [missingColumn, setMissingColumn] = useState<string[]>([]);
  const [pageSize, setPageSize] = useState<number>(10);
  const [uploadFile, setUploadFile] = useState<Blob>();

  const [insuredForm] = FormAntd.useForm();

  useEffect(() => {
    insuredForm.setFieldValue('documentType', 'cuil');
  });

  // Valida que todas las columnas necesarias estén en el excel.
  const validateColumn = (headers: string[], columns: string[]) => {
    const missingColumns: string[] = [];

    columns.forEach((column) => {
      const pattern = column.toLowerCase();
      const found = headers.some((header) =>
        header.toLowerCase().includes(pattern)
      );

      if (!found) missingColumns.push(column);
    });

    return missingColumns;
  };

  // Busca la letra de la columna.
  const columnLetter = (worksheet: any, rowNumber: any, value: string) => {
    const result = Object.keys(worksheet).find((key) => {
      const row = key.slice(1);

      return (
        row == rowNumber && worksheet[key].w.toString() === value.toString()
      );
    });

    return result ? result.charAt(0) : null;
  };

  // Función para transformar y validar los datos del Excel
  const transformData = (data: any[], headers: string[], worksheet: any) => {
    setMissingColumn([]);
    setErrorUploadFile([]);

    const columns = ['Fecha de Nacimiento', 'Apellido y Nombre'];

    const columnsMissing = validateColumn(headers, columns);
    let totalQuantityError = 0;
    let errorExcel: any = [];

    if (columnsMissing.length === 0) {
      const transformedData = data.map((item, index) => {
        let hasError = false;
        const errorsMessage = [];

        const birthday =
          'Fecha de Nacimiento' in item
            ? item['Fecha de Nacimiento']
            : item['fecha de nacimiento'];

        const fullName =
          'Apellido y Nombre' in item
            ? item['Apellido y Nombre']
            : item['apellido y nombre'];

        const documentNumberRaw =
          item['CUIL'] ?? item['cuil'] ?? item['Cuil'] ?? item['CUIL '];
        const documentNumber =
          documentNumberRaw != null && documentNumberRaw !== ''
            ? String(documentNumberRaw).replace(/-/g, '').trim()
            : '';

        const birthdayDateCheck = dayjs(
          new Date((birthday - 25569 + 1) * 86400 * 1000)
        );
        const birthdayDate = birthdayDateCheck.format('DD/MM/YYYY');

        if (!fullName) {
          const message = `El Nombre y Apellido de la columna ${columnLetter(
            worksheet,
            index + 2,
            fullName
          )} no es valido.`;

          errorsMessage.push(message);
          hasError = true;
        }

        if (documentNumber && !validateCuil(documentNumber)) {
          const message = `El CUIL de la columna ${columnLetter(
            worksheet,
            index + 2,
            documentNumberRaw
          )} no es valido.`;

          errorsMessage.push(message);
          hasError = true;
        }

        if (
          birthday === 0 ||
          !birthdayDateCheck.isValid() ||
          birthdayDateCheck.isAfter(dayjs())
        ) {
          const message = `La fecha de nacimiento en la columna ${columnLetter(
            worksheet,
            index + 2,
            birthdayDate
          )} no es válida.`;

          errorsMessage.push(message);
          hasError = true;
        }

        if (hasError) {
          totalQuantityError++;
          errorExcel.push({ row: index + 2, errorsMessage });

          return;
        }

        if (totalQuantityError === 0) {
          return {
            fullName,
            documentNumber: documentNumber || '',
            birthdayDate,
            documentType: 'CUIL',
            gender: 'M',
            key: String(Math.random()),
          };
        }
      });

      if (totalQuantityError === 0) {
        setInsured(transformedData);
        setCanManuallyAdd(false);
        setCanShowUploadingFile(false);
        setErrorUploadFile([]);
        return;
      }

      setErrorUploadFile(errorExcel);

      return;
    }

    setMissingColumn(columnsMissing);
  };

  const readExcelFile = () => {

    if (uploadFile) {
      const reader = new FileReader();

      reader.onload = (e) => {
        const data = new Uint8Array(e?.target?.result as ArrayBuffer);
        const workbook = read(data, { type: 'array' });

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = utils.sheet_to_json(worksheet);
        const headers = utils.sheet_to_json(worksheet, {
          header: 1,
        })[0] as string[];

        transformData(jsonData, headers, worksheet);
      };

      reader.readAsArrayBuffer(uploadFile);
      return;
    }

    const messageError = 'No se ha seleccionado ningún archivo.';
    message.error(messageError);
  };

  const draggerProps: UploadProps = {
    name: 'file',
    multiple: false,
    maxCount: 1,
    fileList: [],
    customRequest: (options: any) => {
      const { file } = options;

      const excelExtensions = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
      ];

      const isExcel = excelExtensions.includes(file.type);

      if (isExcel) {

        setUploadFile(file as Blob);

        message.success(`El archivo ${file.name} fue procesado exitosamente.`);

        return;
      }

      setUploadFile(undefined);
      message.error('Solo se pueden subir archivos Excel.');
    },
  };

  useEffect(() => {
    onInsuredChange?.(insured);
  }, [insured, onInsuredChange]);

  useEffect(() => {
    onUploadFileChange?.(uploadFile);
  }, [uploadFile, onUploadFileChange]);

  const onDeleteInsured = async (key: string) => {
    const filteredInsured = insured.filter((element) => element.key !== key);

    setInsured(filteredInsured);

    if (filteredInsured.length === 0) onClearTable();
  };

  const MOCK_TABLE_COLUMNS_HEADER = useMemo(
    () => [
      {
        title: 'Apellido y Nombre',
        dataIndex: 'fullName',
      },
      {
        title: 'Tipo de Documento',
        dataIndex: 'documentType',
      },
      {
        title: 'Nro. Documento',
        dataIndex: 'documentNumber',
      },
      {
        title: 'F. de Nacimiento',
        dataIndex: 'birthdayDate',
      },
      {
        title: 'Género',
        dataIndex: 'gender',
      },
      {
        title: 'Acción',
        dataIndex: 'action',
        className: 'action-column',
        render: (_: any, data: InsuredTableItem) => (
          <ActionButton
            label=''
            icon={<DeleteOutlined />}
            color='danger'
            type='secondary'
            onClick={() => onDeleteInsured(data.key)}
          />
        ),
      },
    ],
    [onDeleteInsured]
  );

  const onCancelInsuredModal = () => {
    insuredForm.resetFields();
    setCanShowInsuredModal(false);
  };

  const onAddNewInsured = async () => {
    const formFields = insuredForm.getFieldsValue();

    try {
      await insuredForm.validateFields();
      const documentTypeDescription: string = documentTypeList.find(
        (element) => element.value === formFields.documentType
      )?.label!;

      const updatedBirthdayDate = dayjs(formFields.birthdayDate).format(
        'DD/MM/YYYY'
      );

      const genderDescription: string = genderList.find(
        (element) => element.value === formFields.gender
      )?.label!;

      const newInsured = {
        ...formFields,
        birthdayDate: updatedBirthdayDate,
        documentType: documentTypeDescription,
        gender: genderDescription,
        key: String(Math.random()),
      };

      setCanUploadFiles(false);

      setInsured((prevValues) => [...prevValues, newInsured]);
      setCanShowInsuredModal(false);
      insuredForm.resetFields();
    } catch (error) { }
  };

  const onHandlerClearTable = () => {
    if (insured.length === 0) {
      message.warning('La nómina ya se encuentra limpia.');

      return;
    }
    setUploadFile(undefined);
    setCanShowClearTableModal(true);
  };

  const onClearTable = () => {
    setInsured([]);
    setUploadFile(undefined);
    setCanManuallyAdd(true);
    setCanUploadFiles(true);
    setCanShowClearTableModal(false);
  };

  const handleCancel = () => {
    setErrorUploadFile([]);
    setMissingColumn([]);
    setUploadFile(undefined);
    setCanShowUploadingFile(false);
  };

  const openModalUpload = () => {
    if (insured.length > 0) {
      setCanShowUploadModal(true);

      return;
    }

    setCanShowUploadingFile(true);
  };

  const handleDownload = () => {
    const tempLink = document.createElement('a');
    tempLink.href = `${S3_URL}nominaVidaObligatorioPlantilla.xlsx`;
    tempLink.download = 'Nomina Vida Obligatorio Plantilla.xlsx';
    tempLink.target = '_blank';
    tempLink.rel = 'noopener noreferrer';
    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
  };

  return (
    <>
      <ContentSection level='h3' title='Asegurados'>
        <RowSize className='mb-1'>
          <ColSize size={100}>
            <Typography.Text strong>
              Herederos legales, hasta tanto se complete la designación de
              beneficiarios.
            </Typography.Text>
          </ColSize>
        </RowSize>
        <RowSize className='mb-1'>
          <ColSize size={100}>
            <Typography.Text>
              Estimado, usted podrá ingresar a la nómina de asegurados
              manualmente o adjuntar un archivo Excel para su mayor comodidad.
              Si no tiene la planilla modelo, puede descargarla{' '}
              <Typography.Link
                strong
                style={{ color: COLORS.secondary[100] }}
                onClick={handleDownload}
              >
                Presionando Aquí
              </Typography.Link>
              .
              <br />
              Al completarla, deberá respetar el formato indicado en pantalla
              para cada columna. Luego, deberá adjuntarla desde el botón
              seleccionar archivo y finalmente tendrá que presionar{' '}
              <Typography.Text strong style={{ color: COLORS.primary[100] }}>
                Validar Nómina
              </Typography.Text>{' '}
              para confirmar la carga.
              <br />
              Tenga en cuenta que si elige cargar una plantilla Excel se
              inhabilitará en pantalla el botón de carga manual y viceversa.
            </Typography.Text>
          </ColSize>
        </RowSize>
        <RowSize className='mt-2'>
          <ColSize size={100}>
            <ActionButton
              className='btn-accent-outline-barbuss'
              onClick={() => setCanShowInsuredModal(true)}
              icon={<PlusOutlined />}
              disabled={!canManuallyAdd}
              label='Agregar'
              type='secondary'
              style={{
                width: 111,
                height: 32,
                marginRight: 10,
                ...(!canManuallyAdd ? { opacity: 0.3 } : {}),
              }}
            />
            <ActionButton
              className='btn-accent-outline-barbuss'
              disabled={!canUploadFiles}
              icon={<UploadOutlined />}
              onClick={openModalUpload}
              label='Subir'
              type='secondary'
              style={{
                width: 111,
                height: 32,
                marginRight: 10,
                ...(!canUploadFiles ? { opacity: 0.3 } : {}),
              }}
            />
            <ActionButton
              onClick={onHandlerClearTable}
              icon={<DeleteOutlined />}
              label='Limpiar Nómina'
              color='danger'
              type='secondary'
              style={{
                width: 170,
                marginRight: 10,
                height: 32,
              }}
            />
          </ColSize>
        </RowSize>

        <Table
          bordered
          columns={MOCK_TABLE_COLUMNS_HEADER}
          dataSource={insured}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: insured?.length,
            showTotal: (total) =>
              total === 1 ? 'Un asegurado' : `${total} asegurados`,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setPageSize(pageSize);
            },
          }}
          className='person-table mt-1'
          scroll={{ x: 'auto' }}
        />
      </ContentSection>

      <Modal
        centered
        confirmText='Guardar'
        footer
        onCancel={onCancelInsuredModal}
        onOk={onAddNewInsured}
        open={canShowInsuredModal}
        width={876}
      >
        <Form
          formProps={{
            form: insuredForm,
            name: 'insured-form',
          }}
        >
          <RowSize>
            <Typography.Title
              level={4}
              style={{
                margin: 0,
                marginBottom: 10,
                color: COLORS.secondary[100],
              }}
            >
              Nuevo Beneficiario
            </Typography.Title>
          </RowSize>

          <RowSize>
            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: 'Apellido y Nombre',
                  name: 'fullName',
                  rules: validateForm.generateRules({
                    label: 'apellido y nombre',
                    rules: ['required', 'fullName'],
                  }),
                }}
                inputProps={{ placeholder: 'apellido y nombre' }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Tipo de Documento:',
                  name: 'documentType',
                  rules: validateForm.generateRules({
                    label: 'tipo de Documento',
                    rules: ['required'],
                  }),
                }}
                selectProps={{
                  disabled: true,
                  options: documentTypeList,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: 'Número de Documento:',
                  name: 'documentNumber',
                  rules: validateForm.generateRules({
                    label: 'número de documento',
                    rules: ['cuil'],
                  }),
                }}
                inputProps={{ placeholder: '32478963' }}
              />
            </ColSize>

            <ColSize size={25}>
              <DatePickerForm
                formProps={{
                  label: 'F. de Nacimiento:',
                  name: 'birthdayDate',
                  rules: validateForm.generateRules({
                    label: 'fecha de nacimiento',
                    rules: ['required', 'dateNotAfterToday'],
                  }),
                }}
                datePickerProps={{ maxDate: dayjs().subtract(1, 'day') }}
              />
            </ColSize>
            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Género:',
                  name: 'gender',
                  rules: validateForm.generateRules({
                    label: 'género',
                    rules: ['required'],
                  }),
                }}
                selectProps={{
                  disabled: genderList.length < 1,
                  loading: isLoadingGenderList,
                  options: genderList,
                }}
              />
            </ColSize>
          </RowSize>
        </Form>
      </Modal>

      <Modal
        centered
        confirmText='Confirmar'
        footer
        title='Limpiar Nómina'
        onCancel={() => setCanShowClearTableModal(false)}
        onOk={onClearTable}
        open={canShowClearTableModal}
        width={400}
      >
        <Form
          formProps={{
            form: insuredForm,
            name: 'insured-form',
          }}
        >
          <RowSize
            className='mb-1'
            rowProps={{ align: 'middle', justify: 'end' }}
          >
            <ColSize size={100}>
              <Typography.Text>
                ¿Está seguro que desea limpiar la nómina? Esta acción no es
                reversible.
              </Typography.Text>
            </ColSize>
          </RowSize>
        </Form>
      </Modal>

      <Modal
        centered
        confirmText='Confirmar'
        footer
        title='Subir archivo'
        onCancel={() => setCanShowUploadModal(false)}
        onOk={() => {
          setCanShowUploadingFile(true);
          setCanShowUploadModal(false);
        }}
        open={canShowUploadModal}
        width={600}
      >
        <Form
          formProps={{
            form: insuredForm,
            name: 'insured-form',
          }}
        >
          <RowSize
            className='mb-1'
            rowProps={{ align: 'middle', justify: 'end' }}
          >
            <ColSize size={100}>
              <Typography.Text>
                ¿Está seguro que desea subir un nuevo archivo? Se reemplazará la
                nómina actual y la acción no es reversible.
              </Typography.Text>
            </ColSize>
          </RowSize>
        </Form>
      </Modal>

      <Modal
        centered
        open={canShowUploadingFile}
        width={650}
        onCancel={handleCancel}
        onOk={readExcelFile}
        footer
        confirmText='Validar'
      >
        <RowSize className='mb-1'>
          <ColSize size={100}>
            <RowSize rowProps={{ justify: 'center' }}>
              <DraggerUploadFile
                draggerProps={draggerProps}
                formatFile={'excel'}
              />
            </RowSize>
          </ColSize>
        </RowSize>

        {missingColumn.length > 0 && (
          <RowSize rowProps={{ align: 'middle', justify: 'end' }}>
            <ColSize size={100}>
              <Typography.Text strong>
                La importación de la nómina ha sido errónea.
              </Typography.Text>
            </ColSize>
            <ColSize size={100}>
              <Typography.Text>
                No se han encontrado las siguientes columnas:
              </Typography.Text>
            </ColSize>
            {missingColumn?.map((column, errorIndex) => (
              <React.Fragment key={`${column} - ${errorIndex}`}>
                <ColSize size={100}>
                  <ul>
                    <li>
                      <Typography.Text>{column}</Typography.Text>
                    </li>
                  </ul>
                </ColSize>
              </React.Fragment>
            ))}
          </RowSize>
        )}

        {errorUploadFile.length > 0 && (
          <RowSize
            rowProps={{ align: 'middle', justify: 'end', gutter: [0, 0] }}
          >
            <ColSize size={100}>
              <Typography.Text strong>
                La importación de la nómina ha sido errónea.
              </Typography.Text>
            </ColSize>
            <ColSize size={100}>
              <Typography.Text>
                Cantidad de errores: {errorUploadFile?.length}
              </Typography.Text>
            </ColSize>
            {errorUploadFile?.map((error, errorIndex) => (
              <React.Fragment key={`${error?.row} - ${errorIndex}`}>
                <ColSize size={100}>
                  <Typography.Text>
                    Errores en la fila {error?.row}:
                  </Typography.Text>
                </ColSize>
                <ColSize size={100}>
                  <ul>
                    {error?.errorsMessage?.map((errorMessage) => (
                      <li key={errorMessage}>
                        <Typography.Text>{errorMessage}</Typography.Text>
                      </li>
                    ))}
                  </ul>
                </ColSize>
              </React.Fragment>
            ))}
          </RowSize>
        )}
      </Modal>
    </>
  );
};
